import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
import { TaskResourceUserAllocationEditMode } from '~/types';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  omitForbiddenFields,
  buildCreateTaskResourceUserAllocationOptimisticResponse,
  buildUpdateTaskResourceUserAllocationOptimisticResponse
} from '~/modules/resourcing/common/util/taskAllocationSaveUtil';
import { getResourceUserAvailabilityHoursSeriesForDateRangeCallback } from '~/modules/resourcing/common/hooks/useResourceUserAvailabilityHoursSeriesForDateRange';
import { getTaskResourceUserAllocationsSummaryForUserQuery } from '~/modules/tasks/hooks/useGetTaskResourceUserAllocationsSummaryForUser';
import { BULK_GET_TASK_RESOURCE_ALLOCATIONS_QUERY } from '~/modules/resourcing/common/hooks/useBulkGetTaskResourceUserAllocations';
import { getTaskAllocationModificationDetailsOnDrag } from '~/modules/resourcing/common/hooks/useTaskAllocationDragIndicators';
import {
  useRefetchQueriesOnTaskAllocationModification,
  useResourcingChartToolbarContext
} from '~/modules/projects/resourcing-plan/hooks';
import {
  buildRuleConstraintsForPeriod,
  getDateRangeFromScheduleRules,
  getTotalHoursForDateRangeFromScheduleRules,
  mergePeriodRuleIntoScheduleRules
} from '~/modules/resourcing/common/util';
import {
  dateToMidnightUTCObject,
  mapIsoStringtoUtcObject,
  toRepliconDate
} from '~/modules/common/dates/convert';
import { useUserAllocationsSummaryContext } from '../components/ResourceAllocationChartRow/UserAllocationsSummaryContext';

export const tryLoadCachedTaskResourceUserAllocationsSummaryQuery = ({
  proxy,
  query
}) => {
  try {
    return proxy.readQuery(query);
  } catch (e) {
    return null;
  }
};

export const updateTaskResourceUserAllocationsSummaryForUserCache = ({
  periodDetails,
  projectId,
  userId,
  proxy,
  chartDateRange
}) => {
  const query = getTaskResourceUserAllocationsSummaryForUserQuery({
    userId,
    projectId,
    dateRange: chartDateRange
  });
  const results = tryLoadCachedTaskResourceUserAllocationsSummaryQuery({
    proxy,
    query
  });

  if (!results) return;

  const { getTaskResourceUserAllocationsSummaryForUser } = results;

  const {
    periodStartDate,
    periodEndDate,
    newPeriodTaskAllocatedHours: newPeriodHours,
    periodTaskAllocatedHours: originalPeriodHours
  } = periodDetails;

  const totalUserHours = getTotalHoursForDateRangeFromScheduleRules({
    start: periodStartDate,
    end: periodEndDate,
    scheduleRules:
      getTaskResourceUserAllocationsSummaryForUser?.scheduleRules || []
  });

  const scheduleRule = {
    dateRange: {
      startDate: periodStartDate.toISO(),
      endDate: periodEndDate.toISO()
    },
    do: buildRuleConstraintsForPeriod({
      start: periodStartDate,
      end: periodEndDate,
      totalHours:
        (totalUserHours || 0) - (originalPeriodHours || 0) + newPeriodHours,
      quantity: 1,
      excludeWeekdays: [],
      load: 100
    })
  };

  const scheduleRules = getTaskResourceUserAllocationsSummaryForUser
    ? mergePeriodRuleIntoScheduleRules(
        getTaskResourceUserAllocationsSummaryForUser.scheduleRules,
        true
      )(scheduleRule)
    : [scheduleRule];

  const updatedTaskResourceUserAllocationSummary = {
    scheduleRules,
    startDate: scheduleRule.dateRange.startDate,
    endDate: scheduleRule.dateRange.endDate,
    totalHours: newPeriodHours,
    userId
  };

  proxy.writeQuery({
    ...query,
    data: {
      getTaskResourceUserAllocationsSummaryForUser: updatedTaskResourceUserAllocationSummary
    }
  });
};

export const updateCacheOnCreateTaskAllocation = ({
  userId,
  projectId,
  taskId,
  periodDetails,
  chartDateRange,
  hasExistingResourceAllocation,
  resetResourceAllocationChart
}) => (proxy, { data }) => {
  const {
    createTaskResourceUserAllocation: {
      taskResourceUserAllocation,
      syncedResourceAllocation
    }
  } = data;

  proxy.writeQuery({
    query: BULK_GET_TASK_RESOURCE_ALLOCATIONS_QUERY,
    variables: {
      filter: {
        userUri: userId,
        projectUri: projectId,
        taskUris: [taskId]
      }
    },
    data: { taskResourceUserAllocationsForUser: [taskResourceUserAllocation] }
  });

  updateTaskResourceUserAllocationsSummaryForUserCache({
    periodDetails,
    projectId,
    userId,
    proxy,
    chartDateRange
  });

  // Handle resource allocation creation (actuals only user)
  if (!hasExistingResourceAllocation && syncedResourceAllocation) {
    resetResourceAllocationChart();
  }
};

export const updateCacheOnEditTaskAllocation = ({
  userId,
  projectId,
  periodDetails,
  chartDateRange
}) => proxy => {
  updateTaskResourceUserAllocationsSummaryForUserCache({
    periodDetails,
    projectId,
    userId,
    proxy,
    chartDateRange
  });
};

export const filterScheduleRules = scheduleRules =>
  scheduleRules.filter(rule => rule.do.setHours > 0);

const getCreateUpdateTaskAllocationInput = ({
  taskAllocationId,
  taskUri,
  projectUri,
  allocationUserUri,
  roleUri,
  periodDetails,
  periodScheduleRules,
  allocationEditMode
}) => ({
  taskAllocationId,
  taskUri,
  projectUri,
  allocationUserUri,
  roleUri,
  allocationEditMode,
  ...(periodDetails
    ? {
        allocationHours: periodDetails.newPeriodTaskAllocatedHours,
        dateRange: {
          startDate: toRepliconDate(periodDetails.periodStartDate),
          endDate: toRepliconDate(periodDetails.periodEndDate)
        }
      }
    : omitForbiddenFields({ scheduleRules: periodScheduleRules }))
});

export const setSnackBarMessageOnDrag = ({
  updatedTaskAllocationScheduleRules,
  startDate: dragStartDate,
  endDate: dragEndDate,
  setSnackBarState,
  formatMessage,
  taskAllocation,
  canExtendTaskAllocationBeyondProjectAllocation
}) => {
  if (canExtendTaskAllocationBeyondProjectAllocation) return;

  const { startDate, endDate } = taskAllocation;
  const isLeftExpanded = dragStartDate < mapIsoStringtoUtcObject(startDate);
  const isRightExpanded = dragEndDate > mapIsoStringtoUtcObject(endDate);

  const {
    startDate: newStartDate,
    endDate: newEndDate
  } = getDateRangeFromScheduleRules(updatedTaskAllocationScheduleRules);

  if (
    (isLeftExpanded && mapIsoStringtoUtcObject(newStartDate) > dragStartDate) ||
    (isRightExpanded && mapIsoStringtoUtcObject(newEndDate) < dragEndDate)
  ) {
    setSnackBarState({
      open: true,
      message: formatMessage({
        id: 'taskAllocationChangeSnackbar.limitedTimeToAllocationMessage'
      })
    });
  }
};
export const useTaskAssignmentTimelineEditorChangeHandlers = ({
  updateTaskResourceUserAllocation,
  createTaskResourceUserAllocation,
  taskResourceUserAllocation,
  taskResourceEstimate,
  projectId,
  userId,
  openRemoveTaskAllocationDialog,
  setSnackBarState,
  canEditResourceAllocation
}) => {
  const { tenantSlug } = useMeContext();

  const {
    dateRange: chartDateRange,
    isResourceActualModeEnabled
  } = useResourcingChartToolbarContext();
  const refetchQueries = useRefetchQueriesOnTaskAllocationModification({
    userId,
    projectId
  });

  const {
    getResourceUserAvailabilityHoursSeriesForDateRange
  } = getResourceUserAvailabilityHoursSeriesForDateRangeCallback();

  const { formatMessage } = useIntl();

  const {
    userTaskAllocationsSummaryScheduleRules,
    resourceAllocationScheduleRules,
    resetResourceAllocationChart
  } = useUserAllocationsSummaryContext();

  const onPeriodClose = useCallback(
    ({
      allocation,
      periodDetails,
      periodScheduleRules,
      skipCacheUpdate = false
    }) => {
      const { scheduleRules, id, taskUri, projectUri } = allocation;
      const filteredScheduleRules = filterScheduleRules(scheduleRules);
      const newTaskAllocationId =
        !taskResourceUserAllocation &&
        `urn:replicon-tenant:${tenantSlug}:psa-task-allocation:${uuidv4()}`;

      if (taskResourceUserAllocation && filteredScheduleRules.length === 0) {
        openRemoveTaskAllocationDialog();
      } else {
        taskResourceUserAllocation
          ? updateTaskResourceUserAllocation({
              variables: {
                input: getCreateUpdateTaskAllocationInput({
                  taskAllocationId: id,
                  taskUri,
                  projectUri,
                  roleUri: taskResourceUserAllocation.roleUri,
                  periodDetails,
                  periodScheduleRules,
                  allocationEditMode: TaskResourceUserAllocationEditMode.Partial
                })
              },
              optimisticResponse: buildUpdateTaskResourceUserAllocationOptimisticResponse(
                {
                  id,
                  taskResourceEstimate,
                  projectId,
                  userId,
                  scheduleRules
                }
              ),
              update: !skipCacheUpdate
                ? updateCacheOnEditTaskAllocation({
                    userId,
                    projectId,
                    periodDetails,
                    originalTaskResourceUserAllocation: taskResourceUserAllocation,
                    chartDateRange
                  })
                : undefined,
              refetchQueries
            })
          : createTaskResourceUserAllocation({
              variables: {
                input: getCreateUpdateTaskAllocationInput({
                  taskAllocationId: newTaskAllocationId,
                  taskUri: taskResourceEstimate.task.id,
                  projectUri: projectId,
                  allocationUserUri: userId,
                  periodDetails,
                  roleUri: taskResourceEstimate.projectRole?.id
                })
              },
              optimisticResponse: buildCreateTaskResourceUserAllocationOptimisticResponse(
                {
                  id: newTaskAllocationId,
                  taskResourceEstimate,
                  projectId,
                  userId,
                  scheduleRules
                }
              ),
              update: !skipCacheUpdate
                ? updateCacheOnCreateTaskAllocation({
                    userId,
                    projectId,
                    taskId: taskResourceEstimate.task.id,
                    periodDetails,
                    chartDateRange,
                    hasExistingResourceAllocation:
                      !isResourceActualModeEnabled ||
                      Boolean(resourceAllocationScheduleRules?.length),
                    resetResourceAllocationChart
                  })
                : undefined,
              refetchQueries
            });
      }
    },
    [
      chartDateRange,
      createTaskResourceUserAllocation,
      isResourceActualModeEnabled,
      openRemoveTaskAllocationDialog,
      projectId,
      refetchQueries,
      resetResourceAllocationChart,
      resourceAllocationScheduleRules,
      taskResourceEstimate,
      taskResourceUserAllocation,
      tenantSlug,
      updateTaskResourceUserAllocation,
      userId
    ]
  );

  const onAllocationChange = useCallback(
    async ({ startDate, endDate, taskAllocation }) => {
      const dragStartDate = dateToMidnightUTCObject(startDate.toISO());
      const dragEndDate = dateToMidnightUTCObject(endDate.toISO());
      const {
        taskAllocationScheduleRules,
        extendedPeriodScheduleRules,
        collapsedPeriodDetails
      } = await getTaskAllocationModificationDetailsOnDrag({
        startDate: dragStartDate,
        endDate: dragEndDate,
        taskAllocation,
        resourceAllocationScheduleRules,
        userTaskAllocationsSummaryScheduleRules,
        canExtendTaskAllocationBeyondProjectAllocation: canEditResourceAllocation,
        getResourceUserAvailabilityHoursSeriesForDateRange
      });

      setSnackBarMessageOnDrag({
        taskAllocation,
        updatedTaskAllocationScheduleRules: taskAllocationScheduleRules,
        startDate: dragStartDate,
        endDate: dragEndDate,
        setSnackBarState,
        formatMessage,
        canExtendTaskAllocationBeyondProjectAllocation: canEditResourceAllocation
      });

      onPeriodClose({
        allocation: {
          ...taskResourceUserAllocation,
          scheduleRules: taskAllocationScheduleRules
        },
        periodDetails: collapsedPeriodDetails,
        periodScheduleRules: extendedPeriodScheduleRules,
        skipCacheUpdate: true
      });
    },
    [
      resourceAllocationScheduleRules,
      userTaskAllocationsSummaryScheduleRules,
      canEditResourceAllocation,
      getResourceUserAvailabilityHoursSeriesForDateRange,
      setSnackBarState,
      formatMessage,
      onPeriodClose,
      taskResourceUserAllocation
    ]
  );

  return { onPeriodClose, onAllocationChange };
};

export default useTaskAssignmentTimelineEditorChangeHandlers;
